import Row from "@/components/ui-library/grid/Row"
import GridWrapper from "@/components/ui-library/grid/Wrapper"
import { ClinicVisitTypeAvailabilityType, fetchClinicVisitTypeAvailability } from "@/doktorse-api/data"
import { getClinicVisitTypeNames } from "@/storyblok/datasources"
import { cn } from "@/utils/utils"
import { ClinicVisitTypeAvailability } from "./ClinicVisitTypeAvailability"

export type ClinicVisitTypeDateGroup = {
  date: string
  slots: TimeSlotType[]
}

export type TimeSlotType = {
  id: string
  start: string
  end: string
}

export type ClinicVisitType = {
  id: string
  title: string
  shortName: string
  dateGroups: ClinicVisitTypeDateGroup[]
}

export type ClinitI18nLangsType = "sv" | "en"

export type ClinicVisitTypeDto = {
  short_name: string
  link?: { cached_url?: string; url?: string }
}

type ClinicVisitTypeAvailabilitySectionProps = {
  clinicId: string
  visitTypes: ClinicVisitTypeDto[]
  showOther?: boolean
}

// Visit-type display names, keyed by the platform short_name. The values double as the
// allowlist: a short_name not present here is never fetched or rendered on the clinic page.
//
// Source of truth is the Storyblok datasource "clinic-visit-types", read server-side per render
// by getClinicVisitTypeNames(). FALLBACK_VISIT_TYPE_NAMES is a hand-maintained floor so the stable
// core types still resolve if that fetch fails; the datasource wins on merge.
// Snapshot of the production visit_type table (book-svc). Excludes `disabled` types and
// `hide_my_clinic` types (e.g. bookable_phone_call_clinic) since this renders on the clinic page.
const FALLBACK_VISIT_TYPE_NAMES: Record<string, string> = {
  urgent_care: "Lättakuten",
  skin: "Prickmottagning",
  rehab: "Lättakut Muskel/Ledvärk",
  digital_urgent_care: "Digital lättakut",
  book_direct_to_doctor: "Digitalt läkarmöte",
  book_direct_mvc: "Mödravårdcentral",
  digital_direct_midwife: "Digital barnmorska"
}

// Slot times from the platform API are UTC. Bucket and label them in Swedish
// local time so days and times match what staff see in carealot-ui.
const stockholmDateFormatter = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Europe/Stockholm",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
})

export const toStockholmDate = (value: string | Date) => stockholmDateFormatter.format(new Date(value))

export const ClinicVisitTypeAvailabilitySection = async ({
  clinicId,
  visitTypes,
  showOther
}: ClinicVisitTypeAvailabilitySectionProps) => {
  // Only render visit types we know about. This map acts as the allowlist so a misspelled or
  // unknown short_name (free-text in Storyblok) is never fetched or shown. The fallback keeps the
  // core types available even if the datasource read fails, so an outage degrades rather than
  // emptying the section.
  const visitTypeNames: Record<string, string> = {
    ...FALLBACK_VISIT_TYPE_NAMES,
    ...(await getClinicVisitTypeNames())
  }
  const visitTypeShortNames = visitTypes.map(vt => vt.short_name).filter(shortName => shortName in visitTypeNames)

  const now = new Date()
  const MS_PER_DAY = 24 * 60 * 60 * 1000
  const dateRange = Array.from({ length: 3 }, (_, i) => toStockholmDate(new Date(now.getTime() + i * MS_PER_DAY)))

  const { availability, status } = await fetchClinicVisitTypeAvailability(
    clinicId,
    visitTypeShortNames,
    now.toISOString()
  )

  if (
    visitTypeShortNames.length === 0 ||
    availability === null ||
    status !== 200 ||
    (availability != null && availability.length === 0)
  )
    return noAvailability

  const visitTypesAvailability = buildClinicVisitTypeAvailabilityGroup(
    availability,
    visitTypeShortNames,
    dateRange,
    visitTypeNames
  )

  // Every visit type was empty on all rendered dates: render nothing instead of an empty card.
  if (visitTypesAvailability.length === 0) return noAvailability

  return (
    <Row
      className={cn(
        "order-4 my-2 px-4 lg:my-0",
        { "lg:mb-[-6rem] lg:translate-y-[-6rem]": showOther },
        { "lg:mb-[-4rem] lg:translate-y-[-4rem]": !showOther }
      )}
      justifyContent="center"
      justifyContentMobile="center"
    >
      <GridWrapper
        background="white"
        className="row flex w-full flex-col flex-wrap justify-between rounded-2xl shadow-[0px_4px_12px_rgba(0,0,0,0.08)] lg:px-0"
      >
        {visitTypesAvailability.map((visitType, index) => (
          <ClinicVisitTypeAvailability
            key={visitType.shortName}
            visitType={visitType}
            visitTypeLink={visitTypes.find(vt => vt.short_name === visitType.shortName)?.link?.cached_url}
            isFirst={index === 0}
            isLast={index === visitTypesAvailability.length - 1}
            showAccordion={visitTypesAvailability.length > 1}
          />
        ))}
      </GridWrapper>
    </Row>
  )
}

export function buildClinicVisitTypeAvailabilityGroup(
  availability: ClinicVisitTypeAvailabilityType[],
  visitTypeShortNames: string[],
  dateRange: string[],
  // Passed in rather than read from module scope: the names now come from Storyblok at render
  // time, so this stays a pure function of its arguments and remains testable without a network.
  visitTypeNames: Record<string, string>
): ClinicVisitType[] {
  const availabilityByVisitType = availability.reduce((acc: Record<string, ClinicVisitType>, slot, index) => {
    const visitTypeShortName = String(slot.visitTypeShortName)
    if (visitTypeShortNames.includes(visitTypeShortName)) {
      if (!acc[visitTypeShortName]) {
        acc[visitTypeShortName] = {
          id: index.toString(),
          title: visitTypeNames[visitTypeShortName] ?? visitTypeShortName,
          shortName: visitTypeShortName,
          dateGroups: []
        }
      }

      const date = toStockholmDate(slot.start)
      const existingDateIndex = acc[visitTypeShortName].dateGroups.findIndex(s => s.date === date)
      const existing = acc[visitTypeShortName].dateGroups[existingDateIndex]

      if (existing) {
        acc[visitTypeShortName].dateGroups[existingDateIndex] = {
          ...existing,
          slots: [...existing.slots, { id: slot.id, start: slot.start, end: slot.end }]
        }
      } else {
        acc[visitTypeShortName].dateGroups.push({
          date,
          slots: [{ id: slot.id, start: slot.start, end: slot.end }]
        })
      }
    }

    return acc
  }, {})

  return (
    visitTypeShortNames
      .map((shortName, index) => {
        const existing = availabilityByVisitType[shortName]
        const paddedDateGroups = dateRange.map(date => {
          const found = existing?.dateGroups.find(dg => dg.date === date)
          return found ?? { date, slots: [] }
        })
        const title = visitTypeNames[shortName] ?? shortName
        return { id: index.toString(), title, shortName, dateGroups: paddedDateGroups }
      })
      // Drop visit types with no slots on any of the rendered dates: a card showing
      // only "Inga lediga tider" adds no value, so the whole visit type is hidden.
      .filter(visitType => visitType.dateGroups.some(dateGroup => dateGroup.slots.length > 0))
      // Re-index so `id` stays sequential over the visit types actually rendered.
      .map((visitType, index) => ({ ...visitType, id: index.toString() }))
  )
}

export const noAvailability = <></>
